/**
 * Keeps cart line item "Estimated Ship Date" properties in sync with variant backorder metafields.
 */
(function () {
  if (window.__estimatedShipDateSyncInitialized) {
    return;
  }
  window.__estimatedShipDateSyncInitialized = true;

   const ESTIMATED_SHIP_DATE_PROPERTY = 'Pre-Order Est Ship Date';
  const ESTIMATED_SHIP_DATE_KEYS = new Set(['pre-order est ship date','_pre-order est ship date',]);
  let syncInProgress = false;
  let syncDebounceTimer = null;

  const LINE_ITEM_TARGETS = [
    {
      rootId: 'Cart-Drawer',
      section: 'cart-drawer',
      containerSelector: '.product-cart-item--container'
    },
    {
      rootId: 'Cart',
      section: 'main-cart',
      containerSelector: '.cart-items tbody'
    }
  ];

  function getShipDateFromProperties(properties) {
    if (!properties) {
      return '';
    }

    if (Array.isArray(properties)) {
      for (const property of properties) {
        const key = property?.name || property?.first;
        const value = property?.value || property?.last;

        if (key && ESTIMATED_SHIP_DATE_KEYS.has(String(key).toLowerCase()) && value != null && String(value).trim() !== '') {
          return String(value).trim();
        }
      }

      return '';
    }

    for (const [key, value] of Object.entries(properties)) {
      if (ESTIMATED_SHIP_DATE_KEYS.has(String(key).toLowerCase()) && value != null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }

    return '';
  }

  function getActiveLineItemTargets() {
    return LINE_ITEM_TARGETS.filter((target) => document.getElementById(target.rootId));
  }

  async function fetchSectionState() {
    const targets = getActiveLineItemTargets();
    if (!targets.length) {
      return null;
    }

    const sections = [...new Set(targets.map((target) => target.section))].join(',');
    return fetch(`${window.location.pathname}?sections=${sections}`).then((response) => response.json());
  }

  async function updateSyncMarkersFromSections(sectionState) {
    const parsed = sectionState || await fetchSectionState();
    if (!parsed) {
      return;
    }

    getActiveLineItemTargets().forEach(({ section }) => {
      if (!parsed[section]) {
        return;
      }

      const doc = new DOMParser().parseFromString(parsed[section], 'text/html');
      doc.querySelectorAll('.cart-item-ship-date-sync').forEach((freshMarker) => {
        const lineKey = freshMarker.dataset.lineKey;
        if (!lineKey) {
          return;
        }

        document.querySelectorAll(`.cart-item-ship-date-sync[data-line-key="${lineKey}"]`).forEach((existingMarker) => {
          existingMarker.dataset.expectedShipDate = freshMarker.dataset.expectedShipDate || '';
          existingMarker.dataset.cartShipDate = freshMarker.dataset.cartShipDate || '';
          existingMarker.dataset.inventoryQuantity = freshMarker.dataset.inventoryQuantity || '';
        });
      });
    });
  }

  function replaceCartLineItemsFromSections(sectionState) {
    const parsed = sectionState;
    if (!parsed) {
      return;
    }

    getActiveLineItemTargets().forEach(({ rootId, section, containerSelector }) => {
      const root = document.getElementById(rootId);
      if (!root || !parsed[section]) {
        return;
      }

      const doc = new DOMParser().parseFromString(parsed[section], 'text/html');
      const freshContainer = doc.querySelector(containerSelector);
      const existingContainer = root.querySelector(containerSelector);

      if (!freshContainer || !existingContainer) {
        return;
      }

      existingContainer.innerHTML = freshContainer.innerHTML;
    });

    const cartDrawer = document.getElementById('Cart-Drawer');
    cartDrawer?.removeProductEvent?.();
    cartDrawer?.notesToggle?.();
  }

  function getExpectedShipDate(marker) {
    const inventoryQuantity = parseInt(marker.dataset.inventoryQuantity, 10);

    if (!Number.isNaN(inventoryQuantity) && inventoryQuantity > 0) {
      return '';
    }

    return marker.dataset.expectedShipDate || '';
  }

  function getCurrentShipDate(marker, cartItem) {
    return getShipDateFromProperties(cartItem?.properties) || (marker.dataset.cartShipDate || '');
  }

  function getPendingUpdates(cart) {
    const updatesByLineKey = new Map();

    document.querySelectorAll('.cart-item-ship-date-sync').forEach((marker) => {
      const lineKey = marker.dataset.lineKey;
      if (!lineKey || updatesByLineKey.has(lineKey)) {
        return;
      }

      const expectedShipDate = getExpectedShipDate(marker);
      const cartItem = cart.items.find((item) => item.key === lineKey);
      const cartShipDate = getCurrentShipDate(marker, cartItem);

      if (cartShipDate === expectedShipDate) {
        return;
      }

      updatesByLineKey.set(lineKey, {
        lineKey,
        expectedProperty: expectedShipDate
      });
    });

    return Array.from(updatesByLineKey.values());
  }

  function buildUpdatedProperties(cartItem, expectedProperty) {
    const properties = {};
    const shipDateKeysOnLine = [];

    Object.entries(cartItem.properties || {}).forEach(([key, value]) => {
      if (ESTIMATED_SHIP_DATE_KEYS.has(String(key).toLowerCase())) {
        shipDateKeysOnLine.push(key);
        return;
      }

      properties[key] = value == null ? '' : String(value);
    });

    if (expectedProperty) {
      properties[ESTIMATED_SHIP_DATE_PROPERTY] = expectedProperty;
      return properties;
    }

    if (!shipDateKeysOnLine.length) {
      return properties;
    }

    shipDateKeysOnLine.forEach((key) => {
      properties[key] = null;
    });

    if (!shipDateKeysOnLine.some((key) => String(key).toLowerCase() === ESTIMATED_SHIP_DATE_PROPERTY.toLowerCase())) {
      properties[ESTIMATED_SHIP_DATE_PROPERTY] = null;
    }

    return properties;
  }

  async function syncCartEstimatedShipDates() {
    if (syncInProgress) {
      return;
    }

    syncInProgress = true;

    try {
      const sectionState = await fetchSectionState();
      await updateSyncMarkersFromSections(sectionState);

      let cart = await fetch(`${theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' }
      }).then((response) => response.json());

      const pendingUpdates = getPendingUpdates(cart);
      if (!pendingUpdates.length) {
        return;
      }

      for (const update of pendingUpdates) {
        const cartItem = cart.items.find((item) => item.key === update.lineKey);
        if (!cartItem) {
          continue;
        }

        const response = await fetch(`${theme.routes.cart_change_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: cartItem.key,
            quantity: cartItem.quantity,
            properties: buildUpdatedProperties(cartItem, update.expectedProperty)
          })
        });

        if (!response.ok) {
          throw new Error('Unable to update cart line item properties');
        }

        cart = await response.json();
      }

      const refreshedSectionState = await fetchSectionState();
      replaceCartLineItemsFromSections(refreshedSectionState);
    } catch (error) {
      console.error('[Estimated Ship Date] Cart sync failed:', error);
    } finally {
      syncInProgress = false;
    }
  }

  function scheduleSync() {
    clearTimeout(syncDebounceTimer);
    syncDebounceTimer = window.setTimeout(() => {
      syncCartEstimatedShipDates();
    }, 200);
  }

  document.addEventListener('cart-drawer:open', scheduleSync);
  document.addEventListener('cart:item-added', scheduleSync);

  window.addEventListener('load', () => {
    if (document.querySelector('.cart-item-ship-date-sync')) {
      scheduleSync();
    }
  });
})();
